import React, { useEffect, useRef, useState } from 'react';
import { formatTokens } from '../utils/format';

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

// How quickly the display catches up to the estimated live value (lower = calmer).
const LIVE_LERP = 0.008;

/**
 * Live pending-rewards display: gently ticks up between polls, rolls on refresh.
 */
const LiveRewardCounter = ({
  pendingRewards,
  stakedBalance,
  totalStaked,
  dailyEmission,
  decimals = 3,
  className = '',
}) => {
  const anchorRef = useRef({ value: 0, at: 0 });
  const displayRef = useRef(0);
  const snapRef = useRef(null);
  const rafRef = useRef(null);
  const lastFormattedRef = useRef('');
  const [display, setDisplay] = useState(0);
  const [pulsing, setPulsing] = useState(false);

  const staked = Number(stakedBalance) || 0;
  const total = Number(totalStaked) || 0;
  const daily = Number(dailyEmission) || 0;
  const ratePerSecond =
    staked > 0 && total > 0 && daily > 0 ? (daily * (staked / total)) / 86400 : 0;

  useEffect(() => {
    const next = Number(pendingRewards) || 0;
    const prev = anchorRef.current.value;
    const now = performance.now();

    anchorRef.current = { value: next, at: now };

    if (Math.abs(next - prev) > 1e-10) {
      if (next > prev) {
        setPulsing(true);
        setTimeout(() => setPulsing(false), 500);
      }
      snapRef.current = { from: displayRef.current, to: next, start: now, duration: next > prev ? 1200 : 500 };
    }
  }, [pendingRewards]);

  useEffect(() => {
    const loop = (now) => {
      const liveTarget =
        anchorRef.current.value +
        ratePerSecond * ((now - anchorRef.current.at) / 1000);

      let nextDisplay = displayRef.current;
      const snap = snapRef.current;

      if (snap) {
        const t = Math.min(1, (now - snap.start) / snap.duration);
        nextDisplay = snap.from + (snap.to - snap.from) * easeOutCubic(t);
        if (t >= 1) {
          snapRef.current = null;
        }
      } else if (ratePerSecond > 0) {
        nextDisplay += (liveTarget - nextDisplay) * LIVE_LERP;
      } else {
        nextDisplay = liveTarget;
      }

      displayRef.current = nextDisplay;
      const formatted = formatTokens(nextDisplay, decimals);
      if (formatted !== lastFormattedRef.current) {
        lastFormattedRef.current = formatted;
        setDisplay(nextDisplay);
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [ratePerSecond, decimals]);

  return (
    <span
      className={`inline-block tabular-nums transition-colors duration-500 ${
        pulsing ? 'text-green-600 reward-pulse' : 'text-gray-900'
      } ${className}`}
    >
      {formatTokens(display, decimals)}
    </span>
  );
};

export default LiveRewardCounter;
